/**
 * PresetCard Component
 *
 * Individual preset card for the full-page presets dialog.
 * Shows preset details, collapsible enhancements, collapsible inline
 * tag management, and load/delete actions.
 */

import { useState, useRef, useEffect, memo } from 'react';
import { Trash2, ChevronDown, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import type { ImagePreset, TagDefinition } from '../types/image-generator.types';
import { ENHANCEMENT_CATEGORIES } from '../types/image-generator.types';

export interface PresetCardProps {
  preset: ImagePreset;
  tagDefinitions: TagDefinition[];
  sizeOptions: { value: string; label: string }[];
  qualityOptions: { value: string; label: string }[];
  styleOptions: { value: string; label: string }[];
  onLoadPreset: (preset: ImagePreset) => void;
  onDeletePreset: (presetId: string) => void;
  onUpdatePreset: (
    presetId: string,
    updates: Partial<Pick<ImagePreset, 'tags' | 'categoryId'>>,
  ) => void;
  onCreateTag: (name: string) => string;
}

export const PresetCard = memo(function PresetCard({
  preset,
  tagDefinitions,
  sizeOptions,
  qualityOptions,
  styleOptions,
  onLoadPreset,
  onDeletePreset,
  onUpdatePreset,
  onCreateTag,
}: PresetCardProps) {
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [tagSearch, setTagSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const assignedTags = tagDefinitions.filter((t) => preset.tags.includes(t.id));
  const availableTags = tagDefinitions.filter((t) => !preset.tags.includes(t.id));
  const hasEnhancements = ENHANCEMENT_CATEGORIES.some((cat) =>
    preset.enhancements[cat.key]?.trim(),
  );

  const filteredAvailableTags = tagSearch.trim()
    ? availableTags.filter((t) => t.name.toLowerCase().includes(tagSearch.toLowerCase()))
    : availableTags;

  const exactMatch = tagDefinitions.find(
    (t) => t.name.toLowerCase() === tagSearch.trim().toLowerCase(),
  );
  const showCreateOption = tagSearch.trim() && !exactMatch;
  const isAlreadyApplied = exactMatch && preset.tags.includes(exactMatch.id);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showTagDropdown) return;
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowTagDropdown(false);
        setTagSearch('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTagDropdown]);

  const handleAssignTag = (tagId: string) => {
    if (!preset.tags.includes(tagId)) {
      onUpdatePreset(preset.id, { tags: [...preset.tags, tagId] });
    }
    setTagSearch('');
    setShowTagDropdown(false);
  };

  const handleUnassignTag = (tagId: string) => {
    onUpdatePreset(preset.id, { tags: preset.tags.filter((t) => t !== tagId) });
  };

  const handleCreateAndAssign = () => {
    const trimmed = tagSearch.trim();
    if (!trimmed) return;
    const newTagId = onCreateTag(trimmed);
    onUpdatePreset(preset.id, { tags: [...preset.tags, newTagId] });
    setTagSearch('');
    setShowTagDropdown(false);
  };

  return (
    <div className="border border-border rounded-lg p-4 hover:bg-muted/50 transition-colors">
      {/* Header: Name + Delete */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0">
          <h4 className="font-medium truncate">{preset.name}</h4>
        </div>
        <div className="flex items-center gap-1 ml-2 flex-shrink-0">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDeletePreset(preset.id)}
            className="h-7 w-7 text-muted-foreground hover:text-destructive"
            title="Delete preset"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Settings labels */}
      <div className="text-xs text-muted-foreground space-y-0.5 mb-3">
        <p>Size: {sizeOptions.find((s) => s.value === preset.size)?.label || preset.size}</p>
        <p>
          Quality: {qualityOptions.find((q) => q.value === preset.quality)?.label || preset.quality}
        </p>
        <p>Style: {styleOptions.find((s) => s.value === preset.style)?.label || preset.style}</p>
      </div>

      {/* Collapsible enhancements */}
      {hasEnhancements && (
        <Collapsible className="mb-3">
          <CollapsibleTrigger className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors group">
            <ChevronDown className="h-3 w-3 transition-transform group-data-[state=open]:rotate-180" />
            <span>Prompt Enhancements</span>
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-2 pl-4 space-y-1 text-xs">
            {ENHANCEMENT_CATEGORIES.filter((cat) => preset.enhancements[cat.key]?.trim()).map(
              (cat) => (
                <div key={cat.key}>
                  <span className="text-muted-foreground">{cat.label}:</span>{' '}
                  <span className="text-foreground">{preset.enhancements[cat.key]}</span>
                </div>
              ),
            )}
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Collapsible Tags section */}
      <Collapsible className="mb-3">
        <CollapsibleTrigger className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors group">
          <ChevronDown className="h-3 w-3 transition-transform group-data-[state=open]:rotate-180" />
          <span>Tags{assignedTags.length > 0 ? ` (${assignedTags.length})` : ''}</span>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-2 pl-4">
          {/* Assigned tags with remove buttons */}
          {assignedTags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {assignedTags.map((tag) => (
                <Badge key={tag.id} variant="secondary" className="text-xs gap-1">
                  {tag.name}
                  <button
                    type="button"
                    onClick={() => handleUnassignTag(tag.id)}
                    className="ml-0.5 hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}

          {/* Add a tag button + dropdown */}
          <div ref={dropdownRef} className="relative">
            {!showTagDropdown ? (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs gap-1 text-muted-foreground hover:text-primary px-2"
                onClick={() => setShowTagDropdown(true)}
              >
                <Plus className="h-3 w-3" />
                Add a tag
              </Button>
            ) : (
              <>
                <Input
                  value={tagSearch}
                  onChange={(e) => setTagSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.stopPropagation();
                      const trimmed = tagSearch.trim();
                      if (!trimmed) return;
                      if (exactMatch) {
                        if (!preset.tags.includes(exactMatch.id)) {
                          handleAssignTag(exactMatch.id);
                        }
                      } else {
                        handleCreateAndAssign();
                      }
                    }
                    if (e.key === 'Escape') {
                      e.stopPropagation();
                      setShowTagDropdown(false);
                      setTagSearch('');
                    }
                  }}
                  placeholder="Search or create tag..."
                  className="h-8 text-xs"
                  autoFocus
                />
                {(filteredAvailableTags.length > 0 || showCreateOption || isAlreadyApplied) && (
                  <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-md max-h-40 overflow-y-auto">
                    {filteredAvailableTags.map((tag) => (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => handleAssignTag(tag.id)}
                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted transition-colors"
                      >
                        {tag.name}
                      </button>
                    ))}
                    {isAlreadyApplied && filteredAvailableTags.length === 0 && (
                      <span className="block px-3 py-1.5 text-xs text-muted-foreground">
                        Tag already applied
                      </span>
                    )}
                    {showCreateOption && (
                      <button
                        type="button"
                        onClick={handleCreateAndAssign}
                        className="w-full text-left px-3 py-1.5 text-xs text-primary hover:bg-muted transition-colors border-t border-border"
                      >
                        Create &quot;{tagSearch.trim()}&quot;
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Load button */}
      <Button variant="outline" size="sm" onClick={() => onLoadPreset(preset)} className="w-full">
        Load Preset
      </Button>
    </div>
  );
});
